const version = import.meta.env.VITE_GIT_SHA;
const buildDate = import.meta.env.VITE_GIT_TIMESTAMP;

export type DeploymentTargetConfig = {
  // This is the url where THIS application is being served from;
  clientBase: string;

  apiBase: string;
  ftpServiceBaseUrl: string;
  authServiceUrl: string;
  dataServiceUrl: string;
  tpsServiceBase: string;
  webhooksServiceBase: string;
  issServiceBaseUrl: string;
  orgCfgServiceBase: string;
  /**
   * Mints the short-lived Preset guest tokens that the Analytics page needs.
   *
   * The Preset superset domain and the dashboard uuid do not belong here. The
   * service returns both with each guest token, so a change of dashboard is a
   * config change in the service and not a frontend release.
   */
  analyticsServiceBase: string;
  /**
   * LoadBuilder is a separate application with its own deployments, so the
   * product picker links out to it rather than routing. Production points at
   * the marketing page until the per-customer hostnames are designed.
   */
  loadBuilderUrl: string;
  authAccessKey: string;
  publicToken: string;
};

export type RuntimeEnvironment = DeploymentTargetConfig & {
  version: string;
  buildDate: string;
  tag?: string;
  environment: string;
};

export type SupportedDeploymentTargets =
  "development" | "staging" | "test" | "production";

/**
 * Posterity note: the "development" environment can be configured through a `.env.local` file present
 * at the root of the `bfc-web-ui` package.
 */
function viteEnvWithDefault(name: string, defaultValue: string): string {
  const envValue = import.meta.env[name];
  return typeof envValue === "string" ? envValue : defaultValue;
}

const ENVIRONMENT: Record<SupportedDeploymentTargets, DeploymentTargetConfig> =
  {
    development: {
      clientBase: "http://localhost:8100",

      ftpServiceBaseUrl: "/jobs-api",
      tpsServiceBase: viteEnvWithDefault(
        "VITE_TPS_SERVICE_BASE_URL",
        "http://localhost:8801",
      ),
      webhooksServiceBase: viteEnvWithDefault(
        "VITE_WEBHOOKS_SERVICE_BASE_URL",
        "http://localhost:8901",
      ),
      analyticsServiceBase: viteEnvWithDefault(
        "VITE_ANALYTICS_SERVICE_BASE_URL",
        "http://localhost:8700",
      ),
      loadBuilderUrl: "https://dev.transportation.bfc.software/",

      authServiceUrl: viteEnvWithDefault(
        "VITE_AUTH_SERVICE_URL",
        "https://aaa.bfc.software/aaa/v1",
      ),
      authAccessKey: viteEnvWithDefault(
        "VITE_AUTH_SERVICE_ACCESS_TOKEN",
        "bfc_admin_access",
      ),
      issServiceBaseUrl: viteEnvWithDefault(
        "VITE_ISS_SERVICE_BASE_URL",
        "http://localhost:8090",
      ),
      // No local nextgen_config port is standardized yet, so this falls back
      // to beta rather than a guessed one -- override via .env.local once one is.
      orgCfgServiceBase: viteEnvWithDefault(
        "VITE_ORGCFG_SERVICE_BASE_URL",
        "https://beta.config.bfc.software",
      ),

      apiBase: "http://localhost:1985",
      dataServiceUrl: "http://localhost:8080",
      publicToken: "public-token-live-dad2a1e9-b550-4f2d-9ee0-0a33c0e500b7",
    },
    test: {
      clientBase: "http://localhost:8100",

      apiBase: "http://localhost:1985",
      ftpServiceBaseUrl: "https://api.ftp.bfc.software",
      tpsServiceBase: "https://taskplanner.bfc.software",
      webhooksServiceBase: "https://prod.webhooks.bfc.software",
      analyticsServiceBase: "https://prod.analytics.bfc.software",
      loadBuilderUrl: "https://dev.transportation.bfc.software/",
      authServiceUrl: "https://aaa.bfc.software/aaa/v1",
      dataServiceUrl: "http://localhost:8080",
      authAccessKey: "bfc_admin_access",
      issServiceBaseUrl: "https://beta-iss.bfc.software",
      orgCfgServiceBase: "https://beta.config.bfc.software",
      publicToken: "public-token-live-dad2a1e9-b550-4f2d-9ee0-0a33c0e500b7",
    },
    staging: {
      clientBase: "https://beta.bfc-webui.bfc.software",

      apiBase: "",
      ftpServiceBaseUrl: "https://api.ftp.bfc.software",
      tpsServiceBase: "https://alpha.taskplanner.bfc.software",
      webhooksServiceBase: "https://beta.webhooks.bfc.software",
      analyticsServiceBase: "https://beta.analytics.bfc.software",
      loadBuilderUrl: "https://dev.transportation.bfc.software/",

      authServiceUrl: "https://aaa.bfc.software/aaa/v1",
      dataServiceUrl: "https://dakotads.bfc.software",
      authAccessKey: "bfc_admin_access",
      issServiceBaseUrl: "https://beta-iss.bfc.software",
      orgCfgServiceBase: "https://beta.config.bfc.software",
      publicToken: "public-token-live-dad2a1e9-b550-4f2d-9ee0-0a33c0e500b7",
    },
    production: {
      clientBase: "https://bfc-webui.bfc.software",

      apiBase: "",
      ftpServiceBaseUrl: "https://api.ftp.bfc.software",
      tpsServiceBase: "https://taskplanner.bfc.software",
      webhooksServiceBase: "https://prod.webhooks.bfc.software",
      analyticsServiceBase: "https://prod.analytics.bfc.software",
      loadBuilderUrl:
        "https://www.bfcsoftware.com/solutions/transportation/load-planner",

      authServiceUrl: "https://aaa.bfc.software/aaa/v1",
      dataServiceUrl: "https://dakotads.bfc.software",
      authAccessKey: "bfc_admin_access",
      issServiceBaseUrl: "https://iss.bfc.software",
      orgCfgServiceBase: "https://config.bfc.software",
      publicToken: "public-token-live-dad2a1e9-b550-4f2d-9ee0-0a33c0e500b7",
    },
  };

function getStageEnvironment(): DeploymentTargetConfig {
  switch (import.meta.env.VITE_ENVIRONMENT) {
    case "production":
      return ENVIRONMENT.production;
    case "staging":
      return ENVIRONMENT.staging;
    case "test":
      return ENVIRONMENT.test;
    default:
      return ENVIRONMENT.development;
  }
}

export function getEnv(): RuntimeEnvironment {
  const tagValue = import.meta.env.VITE_GIT_TAG;
  const tag =
    typeof tagValue === "string" && tagValue.length > 0 ? tagValue : void 0;

  return {
    ...getStageEnvironment(),
    version,
    tag,
    buildDate,
    environment: import.meta.env.VITE_ENVIRONMENT,
  };
}
